import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { AnimalWithStats, Weight, WeightUnit } from '../data/types';
import { analyzeWeightTrend } from '../utils/weightAnalysis';
import './WeightModal.css';

interface WeightModalProps {
    animal: AnimalWithStats | null;
    onClose: () => void;
    onSubmit: (weight: Weight) => void;
    weightUnit: WeightUnit;
    formatWeight: (grams: number) => string;
    gramsToOunces: (grams: number) => number;
    ouncesToGrams: (ounces: number) => number;
    animalWeights: Weight[];
}

export function WeightModal({
                                animal,
                                onClose,
                                onSubmit,
                                weightUnit,
                                formatWeight,
                                gramsToOunces,
                                ouncesToGrams,
                                animalWeights
                            }: WeightModalProps) {
    const [weightValue, setWeightValue] = useState('');
    const [notes, setNotes] = useState('');
    const [showAlert, setShowAlert] = useState(true);

    // Reset form when animal changes
    useEffect(() => {
        if (animal) {
            setWeightValue('');
            setNotes('');
            setShowAlert(true);
        }
    }, [animal]);

    if (!animal) return null;

    const handleSubmit = () => {
        const value = parseFloat(weightValue);

        if (!value || value <= 0) {
            alert('Please enter a valid weight');
            return;
        }

        // Convert to grams if needed
        const weightGrams = weightUnit === 'ounces' ? ouncesToGrams(value) : value;

        const weight: Weight = {
            id: uuidv4(),
            animalId: animal.id,
            weightGrams: Math.round(weightGrams),
            timestamp: Date.now(),
            notes: notes || undefined,
            createdAt: Date.now(),
        };

        onSubmit(weight);
        onClose();
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const lastWeight = animal.lastWeight?.weightGrams;
    const unitLabel = weightUnit === 'grams' ? 'g' : 'oz';

    // Check for weight alerts
    const weightAlert = analyzeWeightTrend(animalWeights);

    return (
        <div className="modal active" onClick={handleBackdropClick}>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Weigh {animal.name}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {weightAlert && showAlert && (
                    <div className={`weight-alert ${weightAlert.type}`}>
                        <div className="alert-content">
                            <div className="alert-message">{weightAlert.message}</div>
                            {weightAlert.details && (
                                <div className="alert-details">{weightAlert.details}</div>
                            )}
                        </div>
                        <button className="alert-dismiss" onClick={() => setShowAlert(false)}>
                            ×
                        </button>
                    </div>
                )}

                {lastWeight && (
                    <div className="last-weight-info">
                        <div className="info-icon">📊</div>
                        <div>
                            <div className="info-label">Last Weight</div>
                            <div className="info-value">{formatWeight(lastWeight)}</div>
                        </div>
                    </div>
                )}

                <div className="form-group">
                    <label className="form-label">
                        Weight <span className="unit">({unitLabel})</span>
                    </label>
                    <input
                        type="number"
                        className="form-input weight-input"
                        value={weightValue}
                        onChange={(e) => setWeightValue(e.target.value)}
                        placeholder={`Enter weight in ${unitLabel}`}
                        step={weightUnit === 'grams' ? '1' : '0.01'}
                        autoFocus
                    />
                </div>

                <div className="weight-tips">
                    <div className="tip-icon">💡</div>
                    <div className="tip-text">
                        <strong>Tip:</strong> Weigh at the same time each day for consistency.
                        Bottle babies should gain 10-15g per day.
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">
                        Notes <span className="unit">(optional)</span>
                    </label>
                    <input
                        type="text"
                        className="form-input"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any notes about this weigh-in?"
                    />
                </div>

                <button className="submit-btn" onClick={handleSubmit}>
                    Log Weight
                </button>
            </div>
        </div>
    );
}